import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { messageService } from '../../services/messageService';
import { supabase } from '../../lib/supabase';
import ConversationList from './components/ConversationList';
import ChatInterface from './components/ChatInterface';
import EmptyState from './components/EmptyState';

const RealTimeMessaging = () => {
  const { user, userProfile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user && userProfile) {
      loadConversations();
    }
  }, [user, userProfile]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const isCustomer = userProfile?.role === 'customer';
      const isSupplier = userProfile?.role === 'supplier';

      if (!isCustomer && !isSupplier) {
        setLoading(false);
        return;
      }

      // Get user's customer or supplier ID
      let userId = null;
      if (isCustomer) {
        const { data: customerData } = await supabase
          ?.from('customers')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.single();
        userId = customerData?.id;
      } else if (isSupplier) {
        const { data: supplierData } = await supabase
          ?.from('suppliers')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.single();
        userId = supplierData?.id;
      }

      if (!userId) {
        setLoading(false);
        return;
      }

      // Fetch conversations with participant details
      const { data: conversationsData, error } = await supabase
        ?.from('conversations')
        ?.select(`
          *,
          customer:customers!conversations_customer_id_fkey(
            id,
            user:user_profiles!customers_user_id_fkey(
              id,
              full_name,
              avatar_url
            )
          ),
          supplier:suppliers!conversations_supplier_id_fkey(
            id,
            business_name,
            user:user_profiles!suppliers_user_id_fkey(
              id,
              full_name,
              avatar_url
            )
          )
        `)
        ?.or(`customer_id.eq.${userId},supplier_id.eq.${userId}`)
        ?.order('last_message_at', { ascending: false });

      if (error) throw error;

      // Fetch last message and unread count for each conversation
      const conversationsWithDetails = await Promise.all(
        conversationsData?.map(async (conv) => {
          const { data: lastMessage } = await supabase
            ?.from('messages')
            ?.select('message_text, message_type, created_at')
            ?.eq('conversation_id', conv?.id)
            ?.order('created_at', { ascending: false })
            ?.limit(1)
            ?.single();

          const { count: unreadCount } = await supabase
            ?.from('messages')
            ?.select('id', { count: 'exact', head: true })
            ?.eq('conversation_id', conv?.id)
            ?.eq('is_read', false)
            ?.neq('sender_id', user?.id);

          return {
            ...conv,
            lastMessage: lastMessage?.message_text || 'No messages yet',
            lastMessageType: lastMessage?.message_type || 'text',
            lastMessageTime: lastMessage?.created_at || conv?.created_at,
            unreadCount: unreadCount || 0,
            otherParty: isCustomer ? conv?.supplier : conv?.customer
          };
        }) || []
      );

      setConversations(conversationsWithDetails);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
    // Mark messages as read
    if (user?.id) {
      messageService?.markAsRead(conversation?.id, user?.id);
    }
  };

  const handleBackToList = () => {
    setActiveConversation(null);
    loadConversations(); // Refresh to update unread counts
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto h-screen flex flex-col">
        {/* Header */}
        <div className="bg-card border-b border-border px-4 py-4 md:px-6">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
            Messages
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Discuss custom orders and specifications
          </p>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Conversation List - Hidden on mobile when chat is active */}
          <div
            className={`${
              isMobile && activeConversation ? 'hidden' : 'flex'
            } w-full md:w-80 lg:w-96 border-r border-border flex-col bg-card`}
          >
            <ConversationList
              conversations={conversations}
              activeConversation={activeConversation}
              onSelectConversation={handleSelectConversation}
              userRole={userProfile?.role}
            />
          </div>

          {/* Chat Interface */}
          <div
            className={`${
              isMobile && !activeConversation ? 'hidden' : 'flex'
            } flex-1 flex flex-col`}
          >
            {activeConversation ? (
              <ChatInterface
                conversation={activeConversation}
                user={user}
                userProfile={userProfile}
                onBack={handleBackToList}
                isMobile={isMobile}
              />
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeMessaging;